import { OptionType } from "../../../models/global.model";
import Selector, { SelectorType, Props as SelectorProps } from '../Selector/Selector';
import CustomAvatar from '../CustomAvatar';
import StyledCheckbox from '../StyledCheckbox/StyledCheckbox';

export type UserOptionType = OptionType & {
  _id: string;
  name: string;
  email: string;
  imgId?: string;
}

export type UserSelectorType = SelectorType & {
  avatarUrl?: string;
  imgId?: string;
  _id?: string;
  name?: string;
  email?: string;
}

export type UserSelectorProps<T extends UserSelectorType, IsMulti extends boolean = false> = Omit<SelectorProps<T, IsMulti>, 'formatOptionLabel'> & {
  isMulti?: IsMulti;
}

const UserSelector = <T extends UserSelectorType, IsMulti extends boolean = false>({ 
  options, 
  value, 
  onChange, 
  isSelected,
  isMulti = false as IsMulti,
  ...props 
}: UserSelectorProps<T, IsMulti>) => {
  
  const isUserMatch = (userA: T, userB: T): boolean => {
    if (!userA || !userB) return false;
    
    if (userA._id && userB._id) {
      return userA._id === userB._id;
    }
    
    if (userA.value && userB.value) {
      return userA.value === userB.value;
    }
    
    if (userA.value && userB._id) {
      return userA.value === userB._id;
    }
    
    return false;
  };

  const isSelectedInMultiMode = (option: T): boolean => {
    if (!Array.isArray(value)) return false;
    
    return value.some(selectedUser => isUserMatch(selectedUser, option));
  };

  const isSelectedInSingleMode = (option: T): boolean => {
    if (!value || Array.isArray(value)) return false;
    
    const singleValue = value as T;
    return isUserMatch(singleValue, option);
  };

  const checkIfSelected = (option: T): boolean => {
    if (isSelected) {
      return isSelected(option);
    }
    
    if (isMulti) {
      return isSelectedInMultiMode(option);
    }
    
    return isSelectedInSingleMode(option);
  };

  const formatOptionLabel = (option: T, { context }: { context: 'menu' | 'value' }): React.ReactNode => {
    if (context === 'value') {
      return option.label;
    }
    
    if (option.__isNew__) {
      return option.label;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isMulti && (
          <StyledCheckbox 
            checked={checkIfSelected(option)} 
            style={{ marginRight: '8px' }}
          />
        )}
        
        <CustomAvatar 
          src={option.avatarUrl} 
          alt={option.label} 
          size="24px"
          style={{ marginRight: '8px' }}
        />
        
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <Selector<T, IsMulti>
      {...props}
      options={options}
      value={value}
      onChange={onChange}
      isMulti={isMulti}
      isSelected={checkIfSelected}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export default UserSelector;